import React, { useMemo, Component } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Star, Flame, Sparkles, BookOpen, ArrowRight, CheckCircle2, Trophy } from 'lucide-react';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { MODULES } from '../../content';
import type { ModuleId } from '../../content';
import { getModuleIcon } from '../../content/iconResolver';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useDjaySelection } from '../../mascot';
import { Card } from '../ui/Card';
import { ErrorPatternsSection } from './ErrorPatternsSection';
import { MilestonesSection } from './MilestonesSection';
import { PersonalRecordsSection } from './PersonalRecordsSection';
import { PageHero, HeroStatPill } from '../shared/PageHero';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants } from '../shared/animations';
import { WeeklyActivityDots } from '../shared/WeeklyActivityDots';
import { getWeeklyActivityForLearner } from '../../utils/activityHelpers';
import type { AssessmentReport } from '../../assessment';
// ── Static color map (Tailwind JIT safe) ──────────────────────────────
const MODULE_COLORS: Record<ModuleId, {
  border: string;
  fill: string;
  iconBg: string;
  iconText: string;
}> = {
  Metriek: {
    border: 'border-blue-200',
    fill: 'bg-blue-500',
    iconBg: 'bg-blue-100',
    iconText: 'text-blue-600'
  },
  Getalbegrip: {
    border: 'border-indigo-200',
    fill: 'bg-indigo-500',
    iconBg: 'bg-indigo-100',
    iconText: 'text-indigo-600'
  },
  Basisrekenen: {
    border: 'border-orange-200',
    fill: 'bg-orange-500',
    iconBg: 'bg-orange-100',
    iconText: 'text-orange-600'
  },
  Tijd: {
    border: 'border-sky-200',
    fill: 'bg-sky-500',
    iconBg: 'bg-sky-100',
    iconText: 'text-sky-600'
  },
  Geld: {
    border: 'border-yellow-200',
    fill: 'bg-yellow-500',
    iconBg: 'bg-yellow-100',
    iconText: 'text-yellow-600'
  },
  SpellingWW: {
    border: 'border-fuchsia-200',
    fill: 'bg-fuchsia-500',
    iconBg: 'bg-fuchsia-100',
    iconText: 'text-fuchsia-600'
  },
  SpellingRegel: {
    border: 'border-amber-200',
    fill: 'bg-amber-500',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-600'
  },
  Taalbegrip: {
    border: 'border-purple-200',
    fill: 'bg-purple-500',
    iconBg: 'bg-purple-100',
    iconText: 'text-purple-600'
  },
  Studievaardig: {
    border: 'border-teal-200',
    fill: 'bg-teal-500',
    iconBg: 'bg-teal-100',
    iconText: 'text-teal-600'
  },
  Topo: {
    border: 'border-cyan-200',
    fill: 'bg-cyan-500',
    iconBg: 'bg-cyan-100',
    iconText: 'text-cyan-600'
  }
};
const FALLBACK_COLORS = {
  border: 'border-slate-200',
  fill: 'bg-slate-500',
  iconBg: 'bg-slate-100',
  iconText: 'text-slate-600'
};
// ── Component ─────────────────────────────────────────────────────────
interface ProgressPageProps {
  state: AppState;
  selectedGroup: GroupId;
  onViewReport: (report: AssessmentReport) => void;
  onNavigate: (view: string) => void;
}
export function ProgressPage({
  state,
  selectedGroup,
  onViewReport,
  onNavigate
}: ProgressPageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  const currentStreak = state.dailyStreak?.currentWeekDays || 0;
  // Personal records
  const records = useMemo(() => {
    let fastestTime = Infinity;
    let totalCorrect = 0;
    let totalAttempts = 0;
    if (state.questionStates) {
      Object.values(state.questionStates).forEach(qs => {
        qs.history?.forEach(attempt => {
          totalAttempts++;
          if (attempt.isCorrect) {
            totalCorrect++;
            if (attempt.responseTimeMs && attempt.responseTimeMs > 500) {
              fastestTime = Math.min(fastestTime, attempt.responseTimeMs);
            }
          }
        });
      });
    }
    return {
      fastestTime: fastestTime === Infinity ? null : fastestTime,
      accuracy: totalAttempts > 0 ? Math.round(totalCorrect / totalAttempts * 100) : 0,
      longestStreak: state.dailyStreak?.longestWeekStreak || 0,
      perfectSessions: state.dailyStreak?.totalPerfectSessions || 0,
      dailyGoals: state.dailyStreak?.totalDailyGoalsReached || 0
    };
  }, [state.questionStates, state.dailyStreak]);
  // Weekly activity
  const weeklyActivity = useMemo(() => {
    return getWeeklyActivityForLearner(state.questionStates || {});
  }, [state.questionStates]);
  // Module mastery data
  const moduleData = useMemo(() => {
    return MODULES.map(m => ({
      module: m,
      mastery: getModuleMasteryForGroup(m.id),
      answeredCount: getAnsweredQuestions(m.id).length
    }));
  }, [getModuleMasteryForGroup, getAnsweredQuestions]);
  // Overall stats
  const {
    totalAnswered,
    masteredCount,
    overallMastery
  } = useMemo(() => {
    let total = 0;
    let mastered = 0;
    let masterySum = 0;
    moduleData.forEach(({
      mastery,
      answeredCount
    }) => {
      total += answeredCount;
      masterySum += mastery;
      if (mastery >= 85) mastered++;
    });
    return {
      totalAnswered: total,
      masteredCount: mastered,
      overallMastery: Math.round(masterySum / MODULES.length)
    };
  }, [moduleData]);
  return <PageContainer className="space-y-6 sm:space-y-8 pb-20" data-id="element-1809">
      {/* ── Hero ── */}
      <motion.div variants={itemVariants} data-id="element-1810">
        <PageHero title="Mijn Voortgang" subtitle="Bekijk hoe je ervoor staat per vak" gradientClasses="from-indigo-500 via-violet-500 to-purple-500" djayEmotion="celebrating" djayVariant={selectedVariant} icon={TrendingUp} iconBgClass="bg-indigo-400" floatingEmojis={['📊', '⭐']} data-id="element-1811">
          <HeroStatPill icon={Star} data-id="element-1812">Level {state.stats.level}</HeroStatPill>
          <HeroStatPill icon={Sparkles} data-id="element-1813">
            {state.stats.xp.toLocaleString('nl-NL')} XP
          </HeroStatPill>
          <HeroStatPill icon={Flame} data-id="element-1814">{currentStreak} dagen streak</HeroStatPill>
        </PageHero>
      </motion.div>

      {/* ── Quick Stats + Weekly Activity ── */}
      <motion.div variants={itemVariants} data-id="element-1815">
        <Card className="p-4 sm:p-5 bg-white border-2 border-slate-100 rounded-2xl shadow-sm" data-id="element-1816">
          <div className="flex items-center gap-4 sm:gap-6" data-id="element-1817">
            {/* Overall mastery ring */}
            <div className="flex items-center gap-3 flex-shrink-0" data-id="element-1818">
              <div className="relative w-14 h-14" data-id="element-1819">
                <svg className="w-14 h-14 transform -rotate-90" data-id="element-1820">
                  <circle cx="28" cy="28" r="24" fill="none" stroke="#F1F5F9" strokeWidth="4" data-id="element-1821" />
                  <circle cx="28" cy="28" r="24" fill="none" stroke="#6366F1" strokeWidth="4" strokeDasharray="150.8" strokeDashoffset={150.8 - overallMastery / 100 * 150.8} strokeLinecap="round" className="transition-all duration-1000 ease-out" data-id="element-1822" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-1823">
                  <span className="text-sm font-black text-slate-800" data-id="element-1824">
                    {overallMastery}%
                  </span>
                </div>
              </div>
              <div className="hidden sm:block" data-id="element-1825">
                <p className="text-sm font-bold text-slate-800" data-id="element-1826">
                  {masteredCount}/{MODULES.length} vakken
                </p>
                <p className="text-xs text-slate-500 font-medium" data-id="element-1827">
                  {totalAnswered.toLocaleString('nl-NL')} vragen beantwoord
                </p>
              </div>
            </div>

            <div className="w-px h-12 bg-slate-200 flex-shrink-0" data-id="element-1828" />

            {/* Weekly activity */}
            <div className="flex-1 min-w-0" data-id="element-1829">
              <WeeklyActivityDots weeklyActivity={weeklyActivity} color="indigo" data-id="element-1830" />
            </div>
          </div>
        </Card>
      </motion.div>

      {/* ── Vakken Overzicht ── */}
      <motion.div variants={itemVariants} data-id="element-1831">
        <div className="space-y-4" data-id="element-1832">
          <div className="flex items-center gap-3 px-1" data-id="element-1833">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center" data-id="element-1834">
              <BookOpen className="w-5 h-5 text-indigo-600" data-id="element-1835" />
            </div>
            <h2 className="text-xl font-black text-slate-800" data-id="element-1836">
              Vakken Overzicht
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-id="element-1837">
            {moduleData.map(({
            module,
            mastery,
            answeredCount
          }) => {
            const colors = MODULE_COLORS[module.id as ModuleId] || FALLBACK_COLORS;
            const ModuleIcon = getModuleIcon(module.icon);
            const isMastered = mastery >= 85;
            return <Card key={module.id} className={`p-3 sm:p-4 border-2 ${colors.border} bg-white rounded-2xl shadow-sm flex flex-col`} data-id="element-1838">
                  <div className="flex items-center gap-2.5 mb-3" data-id="element-1839">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${colors.iconBg}`} data-id="element-1840">
                      <ModuleIcon className={`w-4.5 h-4.5 ${colors.iconText}`} data-id="element-1841" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-1842">
                      <h4 className="font-bold text-slate-800 text-xs sm:text-sm truncate leading-tight" data-id="element-1843">
                        {module.title}
                      </h4>
                      {answeredCount > 0 && <p className="text-[10px] text-slate-400 font-medium" data-id="element-1844">
                          {answeredCount} vragen
                        </p>}
                    </div>
                  </div>

                  <div className="mt-auto" data-id="element-1845">
                    <div className="flex justify-between items-end mb-1.5" data-id="element-1846">
                      <span className="text-xl font-black text-slate-800 leading-none" data-id="element-1847">
                        {mastery}%
                      </span>
                      {isMastered && <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-1848" />}
                    </div>
                    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-1849">
                      <div className={`h-full ${colors.fill} rounded-full transition-all duration-1000 ease-out`} style={{
                    width: `${mastery}%`
                  }} data-id="element-1850" />
                    </div>
                  </div>
                </Card>;
          })}
          </div>
        </div>
      </motion.div>

      {/* ── Persoonlijke Records ── */}
      <motion.div variants={itemVariants} data-id="element-1851">
        <PersonalRecordsSection fastestTime={records.fastestTime} accuracy={records.accuracy} longestStreak={records.longestStreak} perfectSessions={records.perfectSessions} dailyGoals={records.dailyGoals} data-id="element-1852" />
      </motion.div>

      {/* ── Mijlpalen ── */}
      <motion.div variants={itemVariants} data-id="element-1853">
        <MilestonesSection state={state} selectedGroup={selectedGroup} data-id="element-1854" />
      </motion.div>

      {/* ── Slimme Leertips ── */}
      <motion.div variants={itemVariants} data-id="element-1855">
        <ErrorPatternsSection state={state} selectedGroup={selectedGroup} data-id="element-1856" />
      </motion.div>

      {/* ── Collectie Link ── */}
      <motion.div variants={itemVariants} data-id="element-1857">
        <button onClick={() => onNavigate('collection')} className="w-full text-left" data-id="element-1858">
          <Card className="p-4 sm:p-5 bg-amber-50 border-2 border-amber-200 rounded-2xl flex items-center gap-4 hover:shadow-md hover:border-amber-300 transition-all group shadow-sm" data-id="element-1859">
            <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-1860">
              <Trophy className="w-5 h-5 text-amber-600" data-id="element-1861" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-1862">
              <h3 className="font-bold text-slate-800 text-sm sm:text-base" data-id="element-1863">
                Mijn Collectie
              </h3>
              <p className="text-xs text-slate-500 font-medium" data-id="element-1864">
                Prestaties & Djay-varianten bekijken
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-amber-500 group-hover:translate-x-1 transition-transform flex-shrink-0" data-id="element-1865" />
          </Card>
        </button>
      </motion.div>
    </PageContainer>;
}